/**
 * The form for one section, per kind — 17 Sep 2026.
 *
 * Split out of `BlockEditor` when the handover's components arrived: twelve
 * kinds of form in one component was a four-hundred-line `return` where the
 * thing being edited was three levels of conditional deep.
 *
 * Only the words are here. Colour, padding and alignment belong to every kind
 * and stay on the editor beside this; the defaults for each component come from
 * `newBlock` and are the designer's, so a writer adding a stat row does not
 * choose a type scale.
 */
import { useEffect, useRef, useState } from 'react'
import { RichText } from '@/features/newsletter/RichText'
import {
  overlayOf,
  type BlockAlign,
  type EmailBlock,
  type ImageOverlay,
  type OverlayVAlign,
} from '@/types/emailBlocks'
import { RATIOS, recropEmailImage, uploadEmailImage } from './imageUpload'
import { upperOutsideTags } from './mergeFields'

type Patch = (p: Partial<EmailBlock>) => void

/**
 * A picture: uploaded to our own bucket, or a URL if one is already hosted.
 *
 * The field stays, because a picture that already lives somewhere permanent
 * does not need copying. The button is the path most people want — an image
 * dragged off a desktop has nowhere to live until we give it one.
 */
function ImageField({
  b,
  label,
  onChange,
  formatting,
}: {
  b: EmailBlock
  label: string
  onChange: Patch
  /** Width and link, which a card's thumbnail has no use for. */
  formatting: boolean
}) {
  const picker = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const upload = async (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const out = await uploadEmailImage(file, b.ratio)
      onChange({ src: out.src, srcOriginal: out.original })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The upload failed.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Re-crop from the original rather than from what is on screen.
   *
   * Cropping a crop loses a little more of the picture each time, and after
   * three changes of mind there is nothing left of the edges.
   */
  const recrop = async (ratio: string) => {
    onChange({ ratio })
    if (!b.srcOriginal) return
    setBusy(true)
    setError(null)
    try {
      onChange({
        ratio,
        src: ratio ? await recropEmailImage(b.srcOriginal, ratio) : b.srcOriginal,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The picture could not be re-cropped.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="blk__grid">
        <label className="field">
          <span className="card-label">{label}</span>
          <input
            className="input"
            value={b.src}
            placeholder="Upload, or paste an https:// address"
            onChange={(e) => onChange({ src: e.target.value })}
          />
        </label>
        <label className="field">
          <span className="card-label">Alt text</span>
          <input
            className="input"
            value={b.alt}
            placeholder="What the picture shows, for images-off"
            onChange={(e) => onChange({ alt: e.target.value })}
          />
        </label>
      </div>

      <div className="blk__upload">
        <input
          ref={picker}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          hidden
          onChange={(e) => {
            void upload(e.target.files)
            e.target.value = ''
          }}
        />
        <button
          className="btn btn--ghost btn--tiny"
          disabled={busy}
          onClick={() => picker.current?.click()}
        >
          {busy ? 'Working…' : b.src ? 'Replace picture' : 'Upload a picture'}
        </button>
        {b.src && <img className="blk__thumb" src={b.src} alt="" />}
      </div>

      <div className="blk__who">
        <span className="card-label">Crop</span>
        <div className="blk__chips">
          {RATIOS.map((r) => (
            <button
              key={r.key || 'original'}
              className="chip"
              aria-pressed={(b.ratio || '') === r.key}
              disabled={busy}
              onClick={() => void recrop(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
        {!b.srcOriginal && b.src && (
          <p className="micro">
            This picture was linked rather than uploaded, so there is no original to crop from.
            Upload it to crop it.
          </p>
        )}
      </div>

      {formatting && (
        <>
          <div className="blk__who">
            <span className="card-label">Width</span>
            <div className="blk__chips">
              <button
                className="chip"
                aria-pressed={!b.variant}
                onClick={() => onChange({ variant: '' })}
                title="Fills the text column, inside its margins"
              >
                Column
              </button>
              <button
                className="chip"
                aria-pressed={b.variant === 'full'}
                onClick={() => onChange({ variant: 'full' })}
                title="Edge to edge, with no margin"
              >
                Full width
              </button>
              <button
                className="chip"
                aria-pressed={b.variant === 'natural'}
                onClick={() => onChange({ variant: 'natural' })}
                title="The picture's own size — right for a logo or a small illustration"
              >
                Its own size
              </button>
            </div>
          </div>

          <label className="field">
            <span className="card-label">Links to (optional)</span>
            <input
              className="input"
              value={b.href}
              placeholder="https://…"
              onChange={(e) => onChange({ href: e.target.value })}
            />
          </label>

          {b.src && <OverlayFields b={b} onChange={onChange} />}
        </>
      )}

      <p className="micro">
        Uploaded pictures are hosted by us and stay reachable for as long as the email does.
        Anything over 1 200px is scaled down — an email is read on a phone. Up to 5 MB.
      </p>
      {error && <p className="recform__error">{error}</p>}
    </>
  )
}

const V_ALIGNS: { key: OverlayVAlign; label: string }[] = [
  { key: 'top', label: 'Top' },
  { key: 'middle', label: 'Middle' },
  { key: 'bottom', label: 'Bottom' },
]
const H_ALIGNS: { key: BlockAlign; label: string }[] = [
  { key: 'left', label: 'left' },
  { key: 'center', label: 'centre' },
  { key: 'right', label: 'right' },
]

/**
 * Text over a picture — 13 Oct 2026.
 *
 * A box with its own colour, which can be see-through, and text whose colour,
 * size and place in the picture are the writer's. Place is a three-by-three
 * grid, the way cell alignment works in Word. See `overlaid` in renderEmail.ts
 * for why an email has to draw this as a background rather than a layer.
 */
function OverlayFields({ b, onChange }: { b: EmailBlock; onChange: Patch }) {
  const o = overlayOf(b)
  const set = (p: Partial<ImageOverlay>) => onChange({ overlay: { ...o, ...p } })

  /**
   * Measure the picture's shape whenever it changes.
   *
   * The email draws an overlaid picture as a cell of fixed height, and a
   * background has no height of its own. A crop's ratio wins over this; it
   * matters for "Original" and for a linked picture.
   */
  const src = b.src
  const aspect = o.aspect
  const patchRef = useRef(set)
  patchRef.current = set
  useEffect(() => {
    if (!src) return
    let live = true
    const img = new Image()
    img.onload = () => {
      if (!live || !img.naturalWidth) return
      const a = Math.round((img.naturalHeight / img.naturalWidth) * 10000) / 10000
      if (Math.abs(a - aspect) > 0.0001) patchRef.current({ aspect: a })
    }
    img.src = src
    return () => {
      live = false
    }
  }, [src, aspect])

  return (
    <div className="blk__overlay">
      <label className="field">
        <span className="card-label">Text over the picture (optional)</span>
        <textarea
          className="input"
          rows={2}
          value={o.text}
          placeholder="Leave empty for a plain picture"
          onChange={(e) => set({ text: e.target.value })}
        />
      </label>

      {o.text.trim() && (
        <>
          <div className="blk__style">
            <label className="blk__pagefield">
              <span className="card-label">Text</span>
              <input type="color" value={o.color} onChange={(e) => set({ color: e.target.value })} />
            </label>
            <label className="blk__pagefield">
              <span className="card-label">Size</span>
              <select
                className="input blk__select"
                value={o.size}
                onChange={(e) => set({ size: Number(e.target.value) })}
              >
                {[14, 16, 18, 20, 24, 28, 32, 40, 48, 56, 64].map((n) => (
                  <option key={n} value={n}>
                    {n}px
                  </option>
                ))}
              </select>
            </label>
            <label className="blk__pagefield">
              <span className="card-label">Box</span>
              <input
                type="color"
                value={o.background}
                onChange={(e) => set({ background: e.target.value })}
              />
            </label>
            <label className="blk__pagefield blk__opacity">
              <span className="card-label">
                Box {o.opacity === 0 ? 'off' : `${o.opacity}%`}
              </span>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={o.opacity}
                title="0% is fully see-through — the text straight on the picture"
                onChange={(e) => set({ opacity: Number(e.target.value) })}
              />
            </label>
          </div>

          <div className="blk__who">
            <span className="card-label">Position</span>
            <div className="blk__place" role="group" aria-label="Position of the text">
              {V_ALIGNS.map((v) =>
                H_ALIGNS.map((h) => {
                  const on = o.vAlign === v.key && o.hAlign === h.key
                  return (
                    <button
                      key={`${v.key}-${h.key}`}
                      className="blk__placecell"
                      aria-pressed={on}
                      title={`${v.label} ${h.label}`}
                      onClick={() => set({ vAlign: v.key, hAlign: h.key })}
                    >
                      <span />
                    </button>
                  )
                }),
              )}
            </div>
          </div>
          <p className="micro">
            Outlook on Windows does not show a see-through box — it shows the text straight on the
            picture there, unless the box is at 100%. On a phone the picture is trimmed at the sides
            rather than squeezed, so keep the text away from the edges.
          </p>
        </>
      )}
    </div>
  )
}

/** A row of a component that has rows, edited in place. */
function Items({
  b,
  patch,
  labels,
  max,
  second,
}: {
  b: EmailBlock
  patch: Patch
  /** What the two columns are called for this kind. */
  labels: [string, string]
  max: number
  /** Whether the second column is offered at all. */
  second: boolean
}) {
  const set = (i: number, key: 'a' | 'b', v: string) =>
    patch({ items: b.items.map((it, n) => (n === i ? { ...it, [key]: v } : it)) })

  return (
    <div className="blk__items">
      {b.items.map((it, i) => (
        <div className="blk__item" key={i}>
          <input
            className="input"
            value={it.a}
            placeholder={labels[0]}
            onChange={(e) => set(i, 'a', e.target.value)}
          />
          {second && (
            <input
              className="input"
              value={it.b}
              placeholder={labels[1]}
              onChange={(e) => set(i, 'b', e.target.value)}
            />
          )}
          <button
            className="blk__icon blk__icon--bad"
            title="Remove this row"
            onClick={() => patch({ items: b.items.filter((_, n) => n !== i) })}
          >
            ✕
          </button>
        </div>
      ))}
      {b.items.length < max && (
        <button
          className="btn btn--ghost btn--tiny"
          onClick={() => patch({ items: [...b.items, { a: '', b: '' }] })}
        >
          + Add
        </button>
      )}
    </div>
  )
}

export function BlockFields({ b, patch }: { b: EmailBlock; patch: Patch }) {
  switch (b.kind) {
    case 'text':
      return <RichText value={b.html} onChange={(html) => patch({ html })} resetKey={b.id} />

    case 'heading':
      return (
        <label className="field">
          <span className="card-label">Headline</span>
          <input className="input" value={b.label} onChange={(e) => patch({ label: e.target.value })} />
        </label>
      )

    case 'header':
      return (
        <div className="blk__who">
          <span className="card-label">Wordmark</span>
          <div className="blk__chips">
            <button className="chip" aria-pressed={b.variant !== 'dark'} onClick={() => patch({ variant: 'light' })}>
              On white
            </button>
            <button className="chip" aria-pressed={b.variant === 'dark'} onClick={() => patch({ variant: 'dark' })}>
              On indigo
            </button>
          </div>
        </div>
      )

    case 'image':
      return <ImageField b={b} label="Picture" onChange={patch} formatting />

    case 'button':
      return (
        <div className="blk__grid">
          <label className="field">
            <span className="card-label">Label</span>
            <input className="input" value={b.label} onChange={(e) => patch({ label: e.target.value })} />
          </label>
          <label className="field">
            <span className="card-label">Links to</span>
            <input
              className="input"
              value={b.href}
              placeholder="https://…"
              onChange={(e) => patch({ href: e.target.value })}
            />
          </label>
        </div>
      )

    case 'notice':
      return (
        <div className="blk__grid">
          <label className="field">
            <span className="card-label">Label, in caps</span>
            <input
              className="input"
              value={b.label}
              placeholder="ACCESS RUNS TO"
              // Caps for the words, untouched for anything in braces — see
              // `upperOutsideTags`. This field turned {{plan}} into {{PLAN}}.
              onChange={(e) => patch({ label: upperOutsideTags(e.target.value) })}
            />
          </label>
          <label className="field">
            <span className="card-label">The line itself</span>
            <input
              className="input"
              value={b.html}
              placeholder="14 October 2026 — or {{expires}}"
              onChange={(e) => patch({ html: e.target.value })}
            />
          </label>
        </div>
      )

    case 'stats':
      return (
        <>
          <Items b={b} patch={patch} labels={['17', 'routes']} max={3} second />
          <p className="micro">
            Up to three. A cell with no figure is dropped rather than printed as a dash — a row of
            dashes reports on our data collection, not on the reader&rsquo;s season.
          </p>
        </>
      )

    case 'card':
      return (
        <>
          <ImageField b={b} label="Thumbnail (optional)" onChange={patch} formatting={false} />
          <Items b={b} patch={patch} labels={['Title, place, detail', '']} max={3} second={false} />
          <p className="micro">Three lines: the name, where it is, and the numbers. Without a thumbnail it falls back to the plain card.</p>
        </>
      )

    case 'steps':
      return (
        <>
          <Items b={b} patch={patch} labels={['Your region', 'A line under it (optional)']} max={5} second />
          <p className="micro">Numbered in order. The first is filled; the rest are outlined.</p>
        </>
      )

    case 'footer':
      return (
        <>
          <div className="blk__who">
            <span className="card-label">Which footer</span>
            <div className="blk__chips">
              <button
                className="chip"
                aria-pressed={b.variant !== 'transactional'}
                onClick={() => patch({ variant: 'marketing' })}
              >
                Marketing
              </button>
              <button
                className="chip"
                aria-pressed={b.variant === 'transactional'}
                onClick={() => patch({ variant: 'transactional' })}
              >
                Transactional
              </button>
            </div>
          </div>
          <label className="field">
            <span className="card-label">Postal address</span>
            <input className="input" value={b.html} onChange={(e) => patch({ html: e.target.value })} />
          </label>
          <p className="micro">
            {b.variant === 'transactional'
              ? 'Links to subscription settings. No unsubscribe — there is nothing to unsubscribe from in a receipt.'
              : 'Carries the unsubscribe and preference links, which a marketing send must have.'}
          </p>
        </>
      )

    case 'divider':
    case 'spacer':
      return <p className="micro">Nothing to fill in — the padding below is what sizes it.</p>
  }
}
